import { Campaign, CampaignMessage, CaptionsReadyPusherData, EditPageUpdate, EnvConfigShare, VideoSharePusherData } from '@interfaces'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { getCampaign } from '@api-v2/campaigns'
import { useShareReady } from '@hooks/lib/useShareReady'
import { useCaptionsReady } from '@hooks/lib/useCaptionsReady'
import { MUX_STATUS } from '@constants/constants'
import { reemplaceVariables, validateAndParseCustomCTAUrl } from '@utils/index'
import { useRouter } from 'next/router'

const normalizeDescription = (value?: string): string => {
  if (!value?.trim()) return ''
  return value
}

// Backstop for the SSR-vs-Pusher race: if captions still aren't present this long after mount,
// refetch once (the event may have fired before the client subscribed).
const CAPTIONS_BACKSTOP_DELAY_MS = 5000

interface UseCampaignUpdatesProps {
  campaign: Campaign
  env: EnvConfigShare
  setCampaign: Dispatch<SetStateAction<Campaign>>
  isEditPage: boolean
  campaignUpdate: EditPageUpdate | undefined
  isDynamicVariablesEnabled: boolean
}

interface UseCampaignUpdatesReturn {
  callToAction: CampaignMessage | CampaignMessage[]
  showCta: boolean
  title: string
  description: string
  pageTitle: string | undefined
}

export const useCampaignUpdates = ({
  campaign,
  env,
  setCampaign,
  isEditPage,
  campaignUpdate,
  isDynamicVariablesEnabled
}: UseCampaignUpdatesProps): UseCampaignUpdatesReturn => {
  const router = useRouter()

  const [callToAction, setCallToAction] = useState<CampaignMessage | CampaignMessage[]>(() => {
    if (isDynamicVariablesEnabled) {
      if (campaign?.messages?.length) {
        const messages = campaign.messages.map((message: CampaignMessage) => {
          if (!message?.text) return message
  
          message.text = reemplaceVariables(
            message.text,
            router.query,
            campaign.dynamicVariablesEnable
          )
  
          return message
        })
  
        campaign.messages = messages
      }
    }

    if (campaign?.dynamicVideoProps?.customVariables) {
      if (campaign?.messages?.length) {
        const messages = campaign.messages.map((message: CampaignMessage) => {
          if (!message?.link) return message
  
          message.link = validateAndParseCustomCTAUrl(
            message.link,
            campaign?.dynamicVideoProps?.customVariables as any
          )
  
          return message
        })
  
        campaign.messages = messages
      }
    }  

    return campaign?.featureFlips?.supportMultipleCTAs 
      ? campaign.messages?.filter(item => item.kind === 'link') as CampaignMessage[] 
      : campaign.messages?.find(item => item.kind === 'link') as CampaignMessage
  })

  const [showCta, setShowCta] = useState(!!callToAction)

  const [title, setTitle] = useState(() => {
    if (isDynamicVariablesEnabled) {
      return reemplaceVariables(
        campaign.meta?.heading1,
        router.query,
        true
      )
    }

    return campaign.meta?.heading1
  })

  const [description, setDescription] = useState(() => {
    if (isDynamicVariablesEnabled) {
      return normalizeDescription(reemplaceVariables(
        campaign.meta?.heading2,
        router.query,
        true
      ))
    }

    return normalizeDescription(campaign.meta?.heading2)
  })

  const [pageTitle, setPageTitle] = useState<string>()

  const { shareReady } = useShareReady({
    shareId: campaign?.shareId || '',
    isProcessing: campaign?.meta?.video?.muxStatus === MUX_STATUS.PREPARING || false,
  })

  const hasVideo = !!campaign?.meta?.video
  const hasCaptions = !!campaign?.meta?.video?.muxCaptions?.length

  const { captionsReady } = useCaptionsReady({
    shareId: campaign?.shareId || '',
    isAwaitingCaptions: hasVideo && !hasCaptions,
  })

  const getPageTitle = useCallback((campaign: Campaign) => {
    const branding = campaign?.sendsparkBrandingEnabled
      ? 'Sendspark'
      : campaign?.workspaceName
    const splitCharacter = campaign.meta?.heading1 ? '|' : ''
    return `${branding} ${splitCharacter} ${campaign.meta?.heading1}`.substring(0, 160)
  }, [])

  // Handle campaign updates from edit page
  useEffect(() => {
    if (!isEditPage || !campaignUpdate) return

    if (campaignUpdate.cta) {
      setCallToAction(campaignUpdate.cta)
    }

    if (campaignUpdate.style) {
      setCampaign(prevCampaign => ({
        ...prevCampaign,
        style: {
          ...prevCampaign.style,
          ...campaignUpdate.style,
        },
      }))
    }

    if (typeof campaignUpdate?.title === 'string') {
      setTitle(campaignUpdate.title)
    }

    if (typeof campaignUpdate?.message === 'string') {
      setDescription(normalizeDescription(campaignUpdate.message))
    }

    if (typeof campaignUpdate?.ctaActive === 'boolean') {
      setShowCta(campaignUpdate.ctaActive)
    }

    if (typeof campaignUpdate?.transcriptionsViewEnabled === 'boolean') {
      setCampaign(prevCampaign => ({
        ...prevCampaign,
        transcriptionsViewEnabled: campaignUpdate.transcriptionsViewEnabled,
      }))
    }

    setCampaign(prevCampaign => {
      if (!campaignUpdate.meta) return prevCampaign
      if (!campaignUpdate?.meta?.muxMP4Ready) return prevCampaign

      if (campaignUpdate?.meta?.id !== campaign.meta.video?.id) {
        return {
          ...prevCampaign,
          meta: {
            ...prevCampaign.meta,
            video: campaignUpdate.meta,
          },
        }
      }

      if (!campaignUpdate?.calendar) {
        return {
          ...prevCampaign,
          calendar: undefined,
        }
      }

      if (
        campaignUpdate.calendar?.provider !== campaign.calendar?.provider ||
        campaignUpdate.calendar?.link !== campaign.calendar?.link
      ) {
        return {
          ...prevCampaign,
          calendar: {
            ...prevCampaign.calendar,
            ...campaignUpdate.calendar,
          },
        }
      }

      return prevCampaign
    })
  }, [campaign.meta.video?.id, campaignUpdate, isEditPage, setCampaign])

  // Handle video processing updates
  useEffect(() => {
    const shareId = campaign?.shareId
    if (shareReady && shareId) {
      const updateCampaignDataWhenVideoShareIdIsPrepared = async (
        data: VideoSharePusherData
      ) => {
        if (data?.payload?.videoProcessing === false) {
          const fetchedCampaign = await getCampaign(shareId, undefined, undefined, {
            baseURL: env.apiV2Url,
          })
          setCampaign(fetchedCampaign)
        }

        if (data?.payload?.unbind) {
          shareReady?.unbind(
            shareId,
            updateCampaignDataWhenVideoShareIdIsPrepared
          )
        }
      }
      shareReady.bind(shareId, updateCampaignDataWhenVideoShareIdIsPrepared)
    }
  }, [shareReady])

  // Latest captions presence, read inside the mount-only backstop timeout below.
  const hasCaptionsRef = useRef(hasCaptions)
  hasCaptionsRef.current = hasCaptions

  const isRefetchingRef = useRef(false)
  const pendingRefetchRef = useRef(false)

  // Merge only muxCaptions so the video never reloads. Serialized with a trailing refetch so a
  // burst of per-language events collapses to a couple of calls without missing the last track.
  const refetchCaptions = useCallback(async () => {
    const shareId = campaign?.shareId
    if (!shareId) return
    if (isRefetchingRef.current) {
      pendingRefetchRef.current = true
      return
    }
    isRefetchingRef.current = true
    try {
      const fetchedCampaign = await getCampaign(shareId, undefined, undefined, {
        baseURL: env.apiV2Url,
      })
      const muxCaptions = fetchedCampaign?.meta?.video?.muxCaptions
      if (muxCaptions) {
        setCampaign(prev => {
          // Skip if the video changed between fetches.
          if (!prev?.meta?.video || fetchedCampaign?.meta?.video?.id !== prev.meta.video.id) {
            return prev
          }
          return {
            ...prev,
            meta: {
              ...prev.meta,
              video: { ...prev.meta.video, muxCaptions },
            },
          }
        })
      }
    } finally {
      isRefetchingRef.current = false
      if (pendingRefetchRef.current) {
        pendingRefetchRef.current = false
        // eslint-disable-next-line no-void
        void refetchCaptions()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.shareId, env.apiV2Url, setCampaign])

  // Each language fires its own captions-ready event, so keep listening and unbind on cleanup.
  useEffect(() => {
    const shareId = campaign?.shareId
    if (!captionsReady || !shareId) return undefined

    const onCaptionsReady = (_data: CaptionsReadyPusherData) => {
      // eslint-disable-next-line no-void
      void refetchCaptions()
    }

    captionsReady.bind(shareId, onCaptionsReady)
    return () => {
      captionsReady.unbind(shareId, onCaptionsReady)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsReady])

  // Backstop for the SSR-vs-Pusher race (see CAPTIONS_BACKSTOP_DELAY_MS).
  useEffect(() => {
    if (!hasVideo || hasCaptions) return undefined

    const timeoutId = setTimeout(() => {
      if (!hasCaptionsRef.current) {
        // eslint-disable-next-line no-void
        void refetchCaptions()
      }
    }, CAPTIONS_BACKSTOP_DELAY_MS)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set initial page title
  useEffect(() => {
    setPageTitle(getPageTitle(campaign))
  }, [])

  return {
    callToAction,
    showCta,
    title,
    description,
    pageTitle,
  }
}